import { useState } from 'react';
import {
  App,
  Button,
  Form,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useEnterprises } from '@/hooks/useEnterprises';
import { useAssignEnterprise, useRemoveEnterprise, useRoles, useUser } from '@/hooks/useUsers';
import type { UserDto } from '@/types/user';

const { Text } = Typography;

interface Props {
  user: UserDto | null;
  onClose: () => void;
}

export function UserAssignEnterpriseModal({ user, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<{ enterpriseId: number; roleId: number }>();

  // Reload del user al abrir, para tener las asignaciones frescas
  const { data: detail } = useUser(user?.id ?? null);
  const { data: enterprises } = useEnterprises({ pageSize: 100 });
  const { data: roles } = useRoles();

  const assignMut = useAssignEnterprise();
  const removeMut = useRemoveEnterprise();

  const currentUser = detail ?? user;

  const handleAssign = async () => {
    if (!currentUser) return;
    try {
      const values = await form.validateFields();
      await assignMut.mutateAsync({
        id: currentUser.id,
        request: { enterpriseId: values.enterpriseId, roleId: values.roleId },
      });
      message.success('Enterprise asignada.');
      form.resetFields();
    } catch (err: any) {
      if (err?.errorFields) return;
    }
  };

  const handleRemove = async (enterpriseId: number) => {
    if (!currentUser) return;
    try {
      await removeMut.mutateAsync({ id: currentUser.id, enterpriseId });
      message.success('Enterprise removida.');
    } catch {
      // interceptor
    }
  };

  return (
    <Modal
      title={currentUser ? `Asignaciones de ${currentUser.fullName}` : 'Asignaciones'}
      open={user != null}
      onCancel={() => { form.resetFields(); onClose(); }}
      footer={[
        <Button key="close" onClick={onClose}>Cerrar</Button>,
      ]}
      width={640}
      destroyOnClose
    >
      {currentUser?.isSuperAdmin && (
        <div style={{ marginBottom: 16, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 6 }}>
          <Text type="secondary">
            Este usuario es <Tag color="purple">Super Admin</Tag> y tiene acceso global automatico a todas las Enterprises.
          </Text>
        </div>
      )}

      <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item
          name="enterpriseId"
          rules={[{ required: true, message: 'Requerido' }]}
          style={{ flex: 1 }}
        >
          <Select
            placeholder="Enterprise"
            options={(enterprises?.items ?? []).map((e) => ({
              value: e.id,
              label: e.displayNameEs,
            }))}
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
          />
        </Form.Item>

        <Form.Item
          name="roleId"
          rules={[{ required: true, message: 'Requerido' }]}
        >
          <Select
            placeholder="Rol"
            options={(roles ?? []).map((r) => ({
              value: r.id,
              label: r.nameEs,
            }))}
            style={{ width: 160 }}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAssign}
            loading={assignMut.isPending}
          >
            Asignar
          </Button>
        </Form.Item>
      </Form>

      <List
        header={<Text strong>Asignaciones actuales</Text>}
        bordered
        dataSource={currentUser?.enterpriseAssignments ?? []}
        locale={{ emptyText: 'Sin asignaciones' }}
        renderItem={(a) => (
          <List.Item
            actions={[
              <Popconfirm
                key="del"
                title="Remover esta asignacion?"
                onConfirm={() => handleRemove(a.enterpriseId)}
                okText="Si"
                cancelText="No"
              >
                <Button danger size="small" icon={<DeleteOutlined />} loading={removeMut.isPending}>
                  Remover
                </Button>
              </Popconfirm>,
            ]}
          >
            <Space>
              <Text strong>{a.enterpriseName}</Text>
              <Tag color="blue">{a.roleNameEs}</Tag>
            </Space>
          </List.Item>
        )}
      />
    </Modal>
  );
}
